import { SUBMIT_ACTION, ARCHIVE_ACTION, EDIT_ACTION } from 'constants/constants'
import { useEffect, useState } from 'react'
import { pathOr } from 'ramda'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import RoadmapV2 from 'model/carePlans/carePlan'
import CAREPLAN from 'apollo/queries/CAREPLAN'
import { getStandardDateFormat } from 'utils/helpers/dateHelper'
import useDeleteRoadmapEntity from 'apollo/mutations/CarePlan/DESTORY_CAREPLAN_ENTITY'
import useRoadmapChangeStatus from 'apollo/mutations/CarePlan/CAREPLAN_CHANGE_STATUS'
import useRoadmapUpdate from 'apollo/mutations/CarePlan/CAREPLAN_UPDATE'

const useCarePlanBuilder = (id, modalView, setViewMode) => {
  const navigate = useNavigate()

  const { data, loading } = useQuery(CAREPLAN, {
    variables: { id },
    skip: !id
  })

  const { onDeleteRoadmapEntity, loading: deleteRoadmapLoading } =
    useDeleteRoadmapEntity(false)
  const { onRoadmapChangeStatus, loading: changeStatusLoading } =
    useRoadmapChangeStatus()
  const { onRoadmapUpdate, loading: updateLoading } = useRoadmapUpdate()

  const carePlanData = pathOr({}, ['roadmap_v2'], data)
  const carePlan = new RoadmapV2(carePlanData)

  const {
    archived,
    member: { dob, first_name, organization },
    performed_by,
    suggested_doctors,
    suggested_facilities,
    isProvider,
    isInProgressState,
    isPendingState,
    isResolvedState
  } = carePlan

  const [step, setStep] = useState(0)

  const selectedEntities = isProvider ? suggested_doctors : suggested_facilities

  // permit navigators to send care plans without assigned entities
  const noEntities = !selectedEntities.length

  const normalizedDateOfBirth = getStandardDateFormat(dob)
  const carePlanTitle = first_name
    ? `- ${first_name} - ${organization?.name || ''} - ${normalizedDateOfBirth}`
    : ''

  const onCarePlanAction = async (careplan_id, action) => {
    switch (action) {
      case SUBMIT_ACTION: {
        if (noEntities) {
          return message.error(
            `You can't send a care plan without assigned provider or facility`
          )
        }

        const { isErrorFree } = await onRoadmapChangeStatus({
          id: id || careplan_id,
          with: action
        })

        if (isErrorFree) {
          return modalView ? setViewMode(true) : navigate(`/care-plans/${id}`)
        }

        break
      }
      case ARCHIVE_ACTION: {
        const { isErrorFree } = await onRoadmapUpdate({
          id,
          attributes: { archived: !archived }
        })

        if (isErrorFree && modalView) setViewMode(true)

        break
      }
      case EDIT_ACTION: {
        const { isErrorFree } = await onRoadmapChangeStatus({
          id: id || careplan_id,
          with: action
        })

        if (isErrorFree) {
          return modalView
            ? setViewMode(false)
            : navigate(`/build-care-plan/${careplan_id}`)
        }

        break
      }
      default:
        await onRoadmapChangeStatus({
          id: id || careplan_id,
          with: action
        })
        break
    }
  }

  const removeSelectedEntities = () =>
    Promise.all(
      selectedEntities.map(({ profile }) => {
        const profileId = profile?.id
        return profileId
          ? onDeleteRoadmapEntity({
              roadmap_id: id,
              entity_id: profileId,
              entity_type: isProvider ? 'Provider' : 'Facility'
            })
          : Promise.reject(
              new Error('Chosen provider/facility has been removed')
            )
      })
    )

  const selectEntityType = async entityType => {
    if (performed_by !== entityType && selectedEntities.length) {
      await removeSelectedEntities().catch(e => message.error(e.message))
    }

    const { isErrorFree } = await onRoadmapUpdate({
      id,
      attributes: { performed_by: entityType }
    })

    if (isErrorFree) {
      await onCarePlanAction(id, 'start')
      setStep(1)
    }
  }

  useEffect(() => {
    if (isInProgressState) setStep(1)
    if (isPendingState) setStep(0)
    if (id && isResolvedState && !modalView) navigate(`/care-plans/${id}`)
  }, [
    isInProgressState,
    isPendingState,
    isResolvedState,
    navigate,
    id,
    modalView
  ])

  return {
    loading:
      loading || deleteRoadmapLoading || changeStatusLoading || updateLoading,

    carePlan,
    carePlanTitle,
    onCarePlanAction,

    selectEntityType,

    step,
    setStep,

    noEntities
  }
}

export default useCarePlanBuilder
